import { extname } from 'path';

import { type ResponseCustomFieldValue } from '@air/api/types';
import { type InputFile } from '@air/component-upload-dropzone';
import { type Upload } from '@air/redux-uploader';

import { getFileParentsPaths, getFilePath } from '~/store/uploadingPathToBoardId/utils';
import { containsRestrictedPathChars, sanitizeRestrictedPathChars } from '~/utils/FileUtils';

type UploadGroup = {
  [uploadGroupId: string]: {
    [path: string]: number;
  };
};

export const calculateItemCountFromContentCollectionUploads = (uploads: Upload[]) => {
  const uploadGroups = uploads.reduce<UploadGroup>((acc, upload) => {
    const uploadGroupId = upload.batchId;

    if (!acc[uploadGroupId]) {
      acc[uploadGroupId] = {};
    }

    const path = sanitizeRestrictedPathChars(upload.file.path ? getFilePath(upload.file.path) : '');
    const ancestorsPathArray = getFileParentsPaths(path);

    // Register each ancestor folder so it's counted as a board. Root-level files have no
    // ancestors, so this is a no-op for them — they still get counted as assets below.
    ancestorsPathArray.forEach((currentPath) => {
      if (!acc[uploadGroupId][currentPath]) {
        acc[uploadGroupId][currentPath] = 0;
      }
    });

    // Count the file itself as an asset under its parent path ('' for root-level files).
    acc[uploadGroupId][path] = (acc[uploadGroupId][path] || 0) + 1;

    return acc;
  }, {});

  return Object.values(uploadGroups).reduce((acc, group) => {
    // Boards are the distinct non-empty folder paths; the '' bucket holds root-level assets only.
    const boardCount = Object.keys(group).filter((path) => path !== '').length;
    const assetCount = Object.values(group).reduce((sum, count) => sum + count, 0);

    return acc + boardCount + assetCount;
  }, 0);
};

export const getContentCollectionItemGroupName = (upload: Upload) => {
  const path = sanitizeRestrictedPathChars(upload.file.path ? getFilePath(upload.file.path) : '');

  if (!path) {
    return 'Files';
  }

  return `${upload.batchId}|${path}`;
};

export const valueIfAllAreEqual = <T = ResponseCustomFieldValue | string | undefined | null>(
  values: T[],
): T | undefined => (values.every((val, i) => i === 0 || val === values[i - 1]) ? values[0] : undefined);

export const getSortedFilesAndMetadata = (
  acceptedFiles: InputFile[],
): {
  emptyFiles: InputFile[];
  filesWithNoExtension: InputFile[];
  filesWithRestrictedChars: InputFile[];
  files: InputFile[];
  totalSize: number;
  depthIncrease: number;
} => {
  const emptyFiles: InputFile[] = [];
  const filesWithNoExtension: InputFile[] = [];
  const filesWithRestrictedChars: InputFile[] = [];
  const files: InputFile[] = [];
  let totalSize = 0;
  let depthIncrease = 0;

  acceptedFiles.forEach((file) => {
    if (!file.size) {
      emptyFiles.push(file);
    } else if (!extname(file.name)) {
      filesWithNoExtension.push(file);
    } else if (containsRestrictedPathChars(file.name)) {
      filesWithRestrictedChars.push(file);
      totalSize += file.size;
    } else {
      files.push(file);
      totalSize += file.size;
    }
    depthIncrease = Math.max(depthIncrease, file.path ? file.path.split('/').filter(Boolean).length - 1 : 0);
  });

  return {
    emptyFiles,
    files,
    totalSize,
    filesWithNoExtension,
    filesWithRestrictedChars,
    depthIncrease,
  };
};
